import React, { useEffect, useState } from 'react';
import { Card, Button } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Hash, RefreshCw, Save, Search, ShieldBan, Info } from 'lucide-react';
import { PageHero } from '../components/PageHero';

interface DriverNum {
    id: string;
    username: string;
    ea_id?: string;
    race_number?: number | null;
}

// Offizielle F1 25 Nummern (Geblockt)
const BLOCKED_NUMBERS: Record<number, string> = {
    1: "Max Verstappen",
    4: "Lando Norris",
    5: "Gabriel Bortoleto",
    6: "Isack Hadjar",
    7: "Jack Doohan",
    10: "Pierre Gasly",
    12: "Andrea Kimi Antonelli",
    14: "Fernando Alonso",
    16: "Charles Leclerc",
    18: "Lance Stroll",
    22: "Yuki Tsunoda",
    23: "Alexander Albon",
    27: "Nico Hülkenberg",
    30: "Liam Lawson",
    31: "Esteban Ocon",
    44: "Lewis Hamilton",
    55: "Carlos Sainz",
    63: "George Russell",
    81: "Oscar Piastri",
    87: "Oliver Bearman"
};

export const DriverNumbers: React.FC = () => {
    const { user } = useAuth();
    const [drivers, setDrivers] = useState<DriverNum[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    
    // Quick Edit State (Table)
    const [edits, setEdits] = useState<Record<string, string>>({});

    if (!user || (!user.roles.includes(UserRole.ADMIN) && !user.roles.includes(UserRole.WEBADMIN))) {
        return <Navigate to="/" />;
    }

    const loadData = async () => {
        setLoading(true);
        try {
            // Fix: Typisierung angepasst, da apiRequest die Daten bereits entpackt
            const res = await apiRequest<DriverNum[]>('driver_numbers.php');
            setDrivers(Array.isArray(res) ? res : []);
        } catch(e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const saveNumber = async (userId: string, numStr: string) => {
        const num = numStr === '' ? null : parseInt(numStr);
        
        // 1. Check F1 25 Blocked Numbers
        if (num !== null && BLOCKED_NUMBERS[num]) {
            alert(`Nummer #${num} ist geblockt! (Reserviert für ${BLOCKED_NUMBERS[num]} in F1 25)`);
            return;
        }

        try {
            await apiRequest('driver_numbers.php', 'POST', { userId, number: num });
            
            // Update local state immediately
            setDrivers(prev => prev.map(d => d.id === userId ? { ...d, race_number: num || null } : d));
            
            // Clear edit state for this ID
            const newEdits = { ...edits };
            delete newEdits[userId];
            setEdits(newEdits);

        } catch (e: any) {
            alert("Fehler: " + e.message);
        }
    };

    const handleInputChange = (id: string, val: string) => {
        setEdits(prev => ({ ...prev, [id]: val }));
    };

    const filtered = drivers.filter(d => d.username.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Grid Registry"
                title="Fahrernummern"
                description="Feste Startnummern aller registrierten Fahrer verwalten und reservierte Nummern sicher erkennen."
                icon={Hash}
                accent="#22c55e"
                stats={[
                    { label: 'Fahrer', value: drivers.length },
                    { label: 'Vergeben', value: drivers.filter(driver => driver.race_number).length },
                    { label: 'Reserviert', value: Object.keys(BLOCKED_NUMBERS).length },
                ]}
                actions={<Button variant="secondary" icon={RefreshCw} onClick={loadData}>Aktualisieren</Button>}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Main Table */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input 
                            className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-sm focus:border-f1-red outline-none"
                            placeholder="User suchen..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.23)]">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 uppercase text-xs font-bold">
                                <tr>
                                    <th className="p-4">Fahrer (Account)</th>
                                    <th className="p-4">EA ID</th>
                                    <th className="p-4 w-40 text-center">Startnummer</th>
                                    <th className="p-4 w-20"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {filtered.map(d => {
                                    const currentVal = edits[d.id] !== undefined ? edits[d.id] : (d.race_number || '');
                                    const hasChanged = String(currentVal) !== String(d.race_number || '');
                                    
                                    // Check if current input violates blocked list (live feedback)
                                    const numVal = parseInt(String(currentVal));
                                    const isBlocked = !isNaN(numVal) && BLOCKED_NUMBERS[numVal];

                                    return (
                                        <tr key={d.id} className="hover:bg-gray-800/50 transition-colors">
                                            <td className="p-4 font-bold text-white">{d.username}</td>
                                            <td className="p-4 text-gray-500">{d.ea_id || '-'}</td>
                                            <td className="p-4 text-center">
                                                <div className="relative flex justify-center">
                                                    <input 
                                                        type="number" 
                                                        className={`bg-gray-900 border rounded p-2 w-24 text-center font-mono font-bold text-lg focus:outline-none transition-colors ${
                                                            isBlocked 
                                                                ? 'border-red-500 text-red-500 bg-red-900/10' 
                                                                : hasChanged 
                                                                    ? 'border-yellow-500 text-white'
                                                                    : d.race_number ? 'text-white border-gray-700' : 'text-gray-500 border-gray-800'
                                                        }`}
                                                        value={currentVal}
                                                        onChange={e => handleInputChange(d.id, e.target.value)}
                                                        placeholder="-"
                                                    />
                                                    {isBlocked && (
                                                        <div className="absolute top-full mt-1 bg-red-900 text-red-200 text-[10px] px-2 py-1 rounded z-10 whitespace-nowrap border border-red-700 shadow-lg">
                                                            Geblockt: {BLOCKED_NUMBERS[numVal]}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4 text-right">
                                                {hasChanged && !isBlocked && (
                                                    <button 
                                                        onClick={() => saveNumber(d.id, String(currentVal))}
                                                        className="p-2 bg-green-600 text-white rounded hover:bg-green-500 transition-colors shadow-lg animate-fade-in"
                                                        title="Speichern"
                                                    >
                                                        <Save size={18}/>
                                                    </button>
                                                )}
                                                {isBlocked && (
                                                    <div className="p-2 text-red-500 flex justify-end" title="Nummer gesperrt">
                                                        <ShieldBan size={18}/>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filtered.length === 0 && (
                            <div className="p-8 text-center text-gray-500 italic">
                                {loading ? "Lade Fahrer..." : "Keine Fahrer gefunden."}
                            </div>
                        )}
                    </div>
                </div>

                {/* Blocked Numbers Info Panel */}
                <div>
                    <Card className="sticky top-6 border-l-4 border-l-red-600">
                        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                            <ShieldBan size={20} className="text-red-500"/> Geblockte Nummern
                        </h3>
                        <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                            Diese Nummern sind im Spiel <strong>F1 25</strong> den offiziellen Fahrern vorbehalten und können von Spielern nicht gewählt werden. Sie sind daher hier gesperrt.
                        </p>
                        
                        <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                            {Object.entries(BLOCKED_NUMBERS)
                                .sort((a,b) => parseInt(a[0]) - parseInt(b[0]))
                                .map(([num, name]) => (
                                <div key={num} className="flex items-center justify-between p-2 rounded bg-gray-900/50 border border-gray-800 text-sm">
                                    <span className="text-gray-400 font-medium">{name}</span>
                                    <span className="font-mono font-bold text-red-500 bg-red-900/20 px-2 py-0.5 rounded border border-red-900/30">#{num}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

            </div>
        </div>
    );
};
